import type { IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { createContext, forwardRef, useContext, type HTMLAttributes, type ReactNode } from "react";
import { IconBadge } from "./IconBadge";
import { Text, type TextTone } from "./Typography";

/** Progress of one step: `done` happened, `current` is where the flow stands, `upcoming` has not happened. */
export type TimelineStepState = "done" | "current" | "upcoming";

/** Accent of a step; `danger`/`warning` mark cancelled or denied steps. */
export type TimelineStepTone = "primary" | "danger" | "warning";

/** Reading order of the steps: `oldest-first` draws a progress line through done steps, `newest-first` (a history) keeps every connector neutral. */
export type TimelineOrder = "oldest-first" | "newest-first";

const TimelineOrderContext = createContext<TimelineOrder>("oldest-first");

/** Props of `Timeline`. */
export type TimelineProps = HTMLAttributes<HTMLOListElement> & { order?: TimelineOrder };

/** Vertical ordered list of `TimelineStep`s joined by a connector line. */
export const Timeline = forwardRef<HTMLOListElement, TimelineProps>(function Timeline(
  { order = "oldest-first", className, ...rest },
  ref,
) {
  return (
    <TimelineOrderContext.Provider value={order}>
      <ol {...rest} ref={ref} className={cn("flex flex-col", className)} />
    </TimelineOrderContext.Provider>
  );
});

const DOT: Record<TimelineStepState, Record<TimelineStepTone, string>> = {
  current: { primary: "bg-primary", danger: "bg-danger", warning: "bg-warning" },
  done: { primary: "bg-gray-scale-300", danger: "bg-danger/50", warning: "bg-warning/50" },
  upcoming: { primary: "bg-gray-scale-300", danger: "bg-gray-scale-300", warning: "bg-gray-scale-300" },
};

const CONNECTOR: Record<TimelineOrder, Record<TimelineStepState, string>> = {
  "oldest-first": { done: "bg-primary/50", current: "bg-border", upcoming: "bg-border" },
  "newest-first": { done: "bg-border", current: "bg-border", upcoming: "bg-border" },
};

const TITLE: Record<TimelineStepState, { weight: "medium" | "semibold"; tone: TextTone }> = {
  done: { weight: "medium", tone: "muted" },
  current: { weight: "semibold", tone: "default" },
  upcoming: { weight: "medium", tone: "muted" },
};

/** Props of `TimelineStep`; `icon` swaps the dot for an IconBadge, `time` + `dateTime` render a `<time>`. */
export type TimelineStepProps = Omit<HTMLAttributes<HTMLLIElement>, "title"> & {
  state: TimelineStepState;
  tone?: TimelineStepTone;
  icon?: IconGlyph;
  title: ReactNode;
  time?: ReactNode;
  dateTime?: string;
  description?: ReactNode;
};

/** One step: marker (dot or icon), connector to the next step, title with time, description and extra children. */
export const TimelineStep = forwardRef<HTMLLIElement, TimelineStepProps>(function TimelineStep(
  { state, tone = "primary", icon, title, time, dateTime, description, children, className, ...rest },
  ref,
) {
  const order = useContext(TimelineOrderContext);
  const titleLook = TITLE[state];
  const titleTone: TextTone = tone === "primary" ? titleLook.tone : tone;
  return (
    <li {...rest} ref={ref} aria-current={state === "current" ? "step" : undefined} className={cn("group/step flex gap-3", className)}>
      <div className="flex flex-col items-center">
        {icon ? (
          <IconBadge
            icon={icon}
            size="lg"
            tone={state === "upcoming" ? "neutral" : tone}
            variant={state === "current" && tone === "primary" ? "solid" : "soft"}
          />
        ) : (
          <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", DOT[state][tone])} />
        )}
        <span aria-hidden className={cn("w-0.5 flex-1 group-last/step:hidden", icon ? "mt-2 min-h-4" : "-mb-1.5 min-h-3", CONNECTOR[order][state])} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1 pb-4 group-last/step:pb-0">
        <div className="flex items-baseline justify-between gap-3">
          <Text as="span" weight={titleLook.weight} tone={titleTone} className="min-w-0">
            {title}
          </Text>
          {time ? (
            <Text as="time" size="sm" tone="subtle" dateTime={dateTime} className="shrink-0">
              {time}
            </Text>
          ) : null}
        </div>
        {description ? (
          <Text size="sm" tone="muted">
            {description}
          </Text>
        ) : null}
        {children}
      </div>
    </li>
  );
});
